import { useCallback } from "react";

import type { PaginatedResponse, PaginationParams } from "@prequel/import-react";

import { importClient } from "./sdk/client";

import { type PageOptions, usePage } from "@/api/pagination";

// The activity feed is an app-only endpoint, so its types are not in the generated
// import SDK.
export type ActivityType = "extract" | "load";

export type ActivityStatus = "queued" | "in_progress" | "error" | "completed_with_errors" | "success";

export type ActivityResource = {
  id: string;
  type: ActivityType;
  status: ActivityStatus;
  provider_id: string;
  provider_name: string;
  dataset_id: string;
  dataset_name: string;
  stream_id?: string;
  stream_name?: string;
  total_rows: number;
  submitted_at: string;
  started_at?: string;
  ended_at?: string;
};

type ListActivityResponse = {
  activity: ActivityResource[];
};

const routes = {
  list: "/import/activity",
};

const keys = { all: ["import", "activity"] as const };

export type ActivityFilters = {
  type?: string;
  status?: string;
  provider_id?: string;
  dataset_id?: string;
  stream_id?: string;
};

export const listActivityPage = (
  params: PaginationParams,
  filters?: ActivityFilters,
): Promise<PaginatedResponse<ActivityResource[]>> =>
  importClient.getPaginated<ListActivityResponse>(routes.list, params, filters).then((res) => ({
    data: res.data.activity,
    has_next: res.has_next,
    next_url: res.next_url,
  }));

export function useImportActivity(options?: PageOptions, filters?: ActivityFilters) {
  const type = filters?.type;
  const status = filters?.status;
  const providerID = filters?.provider_id;
  const datasetID = filters?.dataset_id;
  const streamID = filters?.stream_id;

  const fetchPage = useCallback(
    (params: PaginationParams) =>
      listActivityPage(params, {
        type,
        status,
        provider_id: providerID,
        dataset_id: datasetID,
        stream_id: streamID,
      }),
    [type, status, providerID, datasetID, streamID],
  );

  return usePage<ActivityResource>(
    [...keys.all, { type, status, provider_id: providerID, dataset_id: datasetID, stream_id: streamID }],
    fetchPage,
    options,
  );
}
